import {
  ChangeEvent,
  FunctionComponent,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  ImageUploadType,
  OnCropCompleteArgs,
  UploadType,
  useImageCrop,
  useUpload,
} from '../../../hooks';
import { GalleryItem, ImageFile } from '../../../models';
import { getCssVar, isRemoteFileUrl } from '../../../utils';
import { Button, ButtonType } from '../../Button';
import { CircularLoader } from '../../CircularLoader';
import { IconLabel } from '../../IconLabel';
import { ImageCropModal, isCroppableImageSource } from '../../ImageCropModal';
import { Modal } from '../../Modal';
import { TextAreaField } from '../../TextAreaField';

import classes from './FileSettingsModal.module.scss';

interface FileSettingsModalProps {
  galleryItem: GalleryItem;
  onClose: () => void;
  onSave: (galleryItem: GalleryItem) => void;
  hideCaption?: boolean;
}

export const FileSettingsModal: FunctionComponent<FileSettingsModalProps> = memo(
  ({ galleryItem, onClose, onSave, hideCaption }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'imageSettings' });

    const { uploadHandler } = useUpload();

    const { audio, video, pdf, title, image: galleryItemImage } = galleryItem;

    const isFileItem = useMemo(() => Boolean(audio || video || pdf), [audio, pdf, video]);

    const [caption, setCaption] = useState<string>(title ?? '');

    const [rightholder, setRightholder] = useState<string>(galleryItemImage?.rightholder ?? '');

    const [image, setImage] = useState<ImageFile | undefined>(galleryItemImage);

    const [uploading, setUploading] = useState(false);

    const [progress, setProgress] = useState<number>(0);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const onCropComplete = useCallback(({ image }: OnCropCompleteArgs) => {
      setImage((imagePrev) => ({ ...imagePrev, ...image } as ImageFile));
    }, []);

    const { fileToCrop, cropImageId, isCropUploading, openCrop, onCrop, onCropClose } =
      useImageCrop({ onCropComplete });

    const onFileChange = useCallback(
      async ({ target }: ChangeEvent<HTMLInputElement>) => {
        const file = target.files?.[0];

        if (!file) {
          return;
        }

        setUploading(true);
        setProgress(0);

        const { id, isFileTooLarge } = await uploadHandler({
          file,
          type: UploadType.IMAGE,
          imageUploadType: ImageUploadType.ITEM,
          onProgress: setProgress,
        });

        if (!id) {
          toast.error(t(`fileUpload.${isFileTooLarge ? 'file-too-large-error' : 'error'}`));
          setUploading(false);
          return;
        }

        setImage({ id, url: URL.createObjectURL(file) } as ImageFile);
        setUploading(false);
      },
      [t, uploadHandler]
    );

    const onSaveClick = useCallback(() => {
      onSave({ ...galleryItem, title: caption, image: image && { ...image, rightholder } });
    }, [caption, galleryItem, image, onSave, rightholder]);

    const isThumbnailCropping = isCropUploading && image?.id === cropImageId;

    const thumbnailContent = useMemo(() => {
      if (uploading || isThumbnailCropping) {
        return (
          <div className={classes['file-settings-modal__thumbnail-loader']}>
            <CircularLoader {...(uploading && { progress })} sizeRem={2} />
          </div>
        );
      }

      if (!image) {
        return (
          <Button
            type={ButtonType.secondary}
            label={ctxT('add-image')}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          />
        );
      }

      return (
        <div className={classes['file-settings-modal__thumbnail-preview']}>
          <div
            className={classes['file-settings-modal__thumbnail-preview-image']}
            style={{ backgroundImage: `url('${image.url}')` }}
          />
          <div className={classes['file-settings-modal__thumbnail-preview-actions']}>
            {isRemoteFileUrl(image.url) && (
              <IconLabel
                className={classes['file-settings-modal__thumbnail-preview-link']}
                iconId={'arrow-rounded-right-up-box'}
                iconSize={14}
                ariaLabel={t('common.open-in-new-tab')}
                color={getCssVar('--base-link-text-color')}
                hoverColor={getCssVar('--base-link-text-hover-color')}
                onClick={() => window.open(image.url, '_blank', 'noopener, noreferrer')}
              />
            )}

            {isCroppableImageSource(image.url) && (
              <IconLabel
                className={classes['file-settings-modal__thumbnail-preview-crop']}
                iconId={'crop'}
                iconSize={14}
                ariaLabel={t('imageCrop.title')}
                onClick={() => openCrop(image)}
              />
            )}
          </div>
          <span
            className={classes['file-settings-modal__thumbnail-preview-remove']}
            onClick={() => setImage(undefined)}
          ></span>
        </div>
      );
    }, [ctxT, image, isThumbnailCropping, openCrop, progress, t, uploading]);

    const renderThumbnail = useMemo(() => {
      if (!isFileItem) {
        return null;
      }

      return (
        <div className={classes['file-settings-modal__thumbnail']}>
          <div className={classes['file-settings-modal__thumbnail-label']}>{ctxT('thumbnail')}</div>

          {thumbnailContent}

          <input
            ref={fileInputRef}
            type={'file'}
            accept={'image/*'}
            className={classes['file-settings-modal__thumbnail-file']}
            onChange={onFileChange}
            onClick={({ target }) => ((target as HTMLInputElement).value = '')}
          />
        </div>
      );
    }, [ctxT, isFileItem, onFileChange, thumbnailContent]);

    const renderCaption = useMemo(() => {
      if (hideCaption) {
        return null;
      }

      return (
        <TextAreaField
          value={caption}
          label={t('common.caption')}
          placeholder={t('common.caption')}
          maxLength={160}
          onChange={({ target }) => setCaption(target.value)}
          disableLineBreak
        />
      );
    }, [caption, hideCaption, t]);

    const renderCopyright = useMemo(() => {
      if (!image) {
        return null;
      }

      return (
        <TextAreaField
          value={rightholder}
          label={t('common.copyright')}
          placeholder={t('common.copyright')}
          maxLength={160}
          onChange={({ target }) => setRightholder(target.value)}
          disableLineBreak
        />
      );
    }, [image, rightholder, t]);

    const modalTitle = ctxT('title');

    const body = useMemo(
      () => (
        <div className={classes['file-settings-modal']}>
          <div className={classes['file-settings-modal__title']}>{modalTitle}</div>

          {renderThumbnail}

          {renderCaption}

          {renderCopyright}

          <div className={classes['file-settings-modal__footer']}>
            <Button type={ButtonType.secondary} label={t('common.cancel')} onClick={onClose} />
            <Button
              type={ButtonType.primary}
              label={t('common.save')}
              onClick={onSaveClick}
              disabled={uploading}
            />
          </div>
        </div>
      ),
      [
        modalTitle,
        onClose,
        onSaveClick,
        renderCaption,
        renderCopyright,
        renderThumbnail,
        t,
        uploading,
      ]
    );

    return (
      <>
        <Modal
          isOpen
          body={body}
          onClose={onClose}
          contentStyle={{ maxWidth: '30rem' }}
          root={'modal-preview-root'}
          ariaLabel={modalTitle}
        />

        {fileToCrop && (
          <ImageCropModal
            file={fileToCrop}
            title={t('imageCrop.title')}
            onCrop={onCrop}
            onClose={onCropClose}
          />
        )}
      </>
    );
  }
);
